import type { Required } from 'utility-types';

import { request as apiRequest } from '@swf/core/api/request';
import type { RequestOptions } from '@swf/core/types/Request';
import type { Error, Validator } from '@swf/core/utils/validators';

import { TXTWCL_Incorrect_codeX_Please_try_aga_ELL } from './fiscalCode.messages.json';

export interface UserData {
  firstName: string;
  lastName: string;
  dob: Date;
  gender: string | null;
}

interface Isounds {
  consonants: string;
  vowels: string;
}

export const length = 16;

const separate = (name: string): Isounds => {
  const vowels = 'AEIOU',
    chars = name.split('');

  return {
    consonants: chars.filter((c) => /[A-Z]/.test(c) && vowels.indexOf(c) < 0).join(''),
    vowels: chars.filter((c) => /[A-Z]/.test(c) && vowels.indexOf(c) >= 0).join('')
  };
};
const padX = (value: string): string => value + Array(3 + 1 - value.length).join('X');

const getName = (name: string, skipConsonants?: boolean) => {
  const n = separate(name);
  let result =
    skipConsonants && n.consonants.length > 3
      ? n.consonants.substring(0, 1) + n.consonants.substring(2, 4)
      : n.consonants.substr(0, 3);

  if (result.length < 3) {
    result += n.vowels.substr(0, 3 - result.length);
  }

  return result.length < 3 ? padX(result) : result;
};

const partialFiscalCode = ({ firstName, lastName, dob, gender }: Partial<UserData>): string => {
  if (!firstName || !lastName || !dob || !gender) {
    return '';
  }

  let result = getName(lastName.toUpperCase()) + getName(firstName.toUpperCase(), true);
  result += dob.getFullYear().toString().slice(-2);
  result += 'ABCDEHLMPRST'[dob.getMonth()];

  const day = dob.getDate();
  if (gender === 'F') {
    result += day + 40;
  } else {
    result += day.toString().length === 1 ? `0${day}` : day;
  }

  return result;
};

const validateCheckChar = (value: string) => {
  const odd = [
      1, 0, 5, 7, 9, 13, 15, 17, 19, 21, 2, 4, 18, 20, 11, 3, 6, 8, 12, 14, 16, 10, 22, 25, 24, 23
    ],
    checkChar = value.slice(-1);

  const check = String.fromCharCode(
    (value
      .substring(0, value.length - 1)
      .split('')
      .map((char: string, index: number) => {
        const code = char.charCodeAt(0) - (/\d/.test(char) ? 48 : 65);

        if (index % 2) {
          return code;
        }

        return odd[code];
      })
      .reduce((a, b) => a + b) %
      26) +
      65
  );

  return check === checkChar;
};

export const fiscalCode =
  (data: Partial<UserData>) =>
  (value: string): Error => {
    if (!value || value.length !== 16) {
      return { valid: false, message: TXTWCL_Incorrect_codeX_Please_try_aga_ELL };
    }

    const accountDataCode = partialFiscalCode(data);
    if (!accountDataCode) {
      return { valid: false, message: TXTWCL_Incorrect_codeX_Please_try_aga_ELL };
    }

    return {
      valid:
        value.substring(0, accountDataCode.length) === accountDataCode && validateCheckChar(value),
      message: TXTWCL_Incorrect_codeX_Please_try_aga_ELL
    };
  };

export const checkFiscalCode = (
  options: Required<Partial<RequestOptions>, 'meta'>,
  message: Error['message']
) => {
  const checkFiscalCode: Validator = async (value: string): Promise<Error> => {
    if (value?.length === 16) {
      const {
        body: { unique }
      } = await apiRequest({
        ...options,
        service: 'wc3.user.check.fiscalCode',
        body: { fiscalCode: value }
      });
      if (!unique) {
        return {
          valid: false,
          message
        };
      } else {
        return {
          valid: true
        };
      }
    }

    return {
      valid: false,
      message
    };
  };

  return checkFiscalCode as Validator;
};
